// Training API Client - Frontend service for training system

import { API_BASE_URL } from '../config/settings';

export interface TrainingStats {
  audienceClassification: {
    total: number;
    validated: number;
    pending: number;
  };
  prefill: {
    total: number;
  };
  playbook: {
    total: number;
  };
  readyForTraining: {
    audienceClassification: boolean;
    prefill: boolean;
    playbook: boolean;
  };
}

export interface TrainingItem {
  id: string;
  resourceId: string;
  title?: string;
  summary?: string;
  category?: string;
  classification: {
    purpose?: string;
    audienceMatches?: any[];
    problems?: string[];
    angles?: string[];
    insights?: string[];
    awarenessLevel?: string;
    funnelStage?: string;
  };
  classifiedAt: number;
  humanValidated: boolean;
}

export interface FeedbackRequest {
  resourceId: string;
  feedbackType: 'audience_classification' | 'prefill' | 'playbook';
  originalData: any;
  correctedData?: any;
  isCorrect: boolean;
  userNotes?: string;
}

class TrainingApi {
  private baseUrl = `${API_BASE_URL}/audience/training`;

  async getStats(): Promise<TrainingStats> {
    const res = await fetch(`${this.baseUrl}/stats`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get training stats');
    }

    return json.data;
  }

  async getTrainingItems(filters?: {
    feedbackType?: 'audience_classification' | 'prefill' | 'playbook';
    validated?: boolean;
    audienceId?: string;
    limit?: number;
    offset?: number;
  }): Promise<TrainingItem[]> {
    const params = new URLSearchParams();

    if (filters?.feedbackType) params.set('feedbackType', filters.feedbackType);
    if (filters?.validated !== undefined) params.set('validated', filters.validated.toString());
    if (filters?.audienceId) params.set('audienceId', filters.audienceId);
    if (filters?.limit) params.set('limit', filters.limit.toString());
    if (filters?.offset) params.set('offset', filters.offset.toString());

    const url = params.toString() ? `${this.baseUrl}/items?${params}` : `${this.baseUrl}/items`;
    const res = await fetch(url);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get training items');
    }

    return json.data;
  }

  async getNextSimilar(resourceId: string): Promise<TrainingItem | null> {
    const res = await fetch(`${this.baseUrl}/next-similar/${resourceId}`);
    const json = await res.json();

    if (!json.success) {
      if (res.status === 404) return null;
      throw new Error(json.error || 'Failed to get next similar');
    }

    return json.data;
  }

  async saveFeedback(feedback: FeedbackRequest): Promise<string> {
    const res = await fetch(`${this.baseUrl}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(feedback)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to save feedback');
    }

    return json.data.id;
  }

  async bulkValidate(resourceIds: string[], isCorrect: boolean): Promise<number> {
    const res = await fetch(`${this.baseUrl}/bulk-validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resourceIds, isCorrect })
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to bulk validate');
    }

    return json.data.count;
  }

  async exportTrainingData(modelType: 'audience_classification' | 'prefill' | 'playbook', format: 'jsonl' | 'csv' = 'jsonl'): Promise<void> {
    const res = await fetch(`${this.baseUrl}/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ modelType, format })
    });

    if (!res.ok) {
      throw new Error('Failed to export training data');
    }

    // Download file
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training_${modelType}_${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const trainingApi = new TrainingApi();